import { FC, Fragment } from 'react';
import CellValue from '@app/shared/components/cell-value/CellValue';
import {
  amountRules,
  shortDateViewRules,
} from '@app/shared/components/cell-value/CellValueConfigurations';
import { useAppSelector } from '@core/hooks/redux-hooks';
import { useLocale } from '@app/core/hooks/useLocale';
import TableSectionValuesPerForecast from './TableSectionValuesPerForecast';
import TableSpacerRow from '@app/shared/components/table/TableSpacerRow';

type TableSectionValuesPerHistoricalForecastEventProps = {
  columnsCount: number;
  testId?: string;
};

const TableSectionHistoricalInfusions: FC<TableSectionValuesPerHistoricalForecastEventProps> = ({
  columnsCount,
  testId,
}) => {
  const { l } = useLocale();

  const waterfallCasesResults = useAppSelector(
    (state) => state.pwerm2Calculation.calculatedResults.waterfall.cases
  );

  const maximumNumberOfEvents = Object.values(waterfallCasesResults).reduce(
    (max, caseItem) => Math.max(max, caseItem.historicInstitutionalAdditions?.length ?? 0),
    0
  );

  return (
    <>
      <tr>
        <th className="table-primary__cell--header-tertiary">{l('_HistoricalInvestments')}</th>
        <th colSpan={columnsCount - 1} className="table-primary__cell--header-tertiary" />
      </tr>
      <TableSectionValuesPerForecast
        title={l('_InitialInvestmentDate')}
        renderSpacer={false}
        testId={testId ? `${testId}-initial-date` : undefined}
        renderCell={({ caseItem, forecastIndex }) =>
          forecastIndex === 0 ? (
            <CellValue
              value={waterfallCasesResults[caseItem.caseId].investmentDate}
              {...shortDateViewRules}
            />
          ) : null
        }
      />
      <TableSectionValuesPerForecast
        title={l('_InitialInvestmentCost')}
        renderSpacer={false}
        testId={testId ? `${testId}-initial-amount` : undefined}
        renderCell={({ caseItem, forecastIndex }) =>
          forecastIndex === 0 ? (
            <CellValue
              value={waterfallCasesResults[caseItem.caseId].institutionalInvestment}
              {...amountRules}
            />
          ) : null
        }
      />
      {Array.from(Array(maximumNumberOfEvents).keys()).map((eventsIndex) => (
        <Fragment key={eventsIndex}>
          <TableSpacerRow />
          <TableSectionValuesPerForecast
            title={l('_InfusionDate')}
            renderSpacer={false}
            testId={testId ? `${testId}-infusion-date-${eventsIndex}` : undefined}
            renderCell={({ caseItem, forecastIndex }) => {
              if (forecastIndex !== 0) {
                return null;
              }
              const event =
                waterfallCasesResults[caseItem.caseId].historicInstitutionalAdditions?.[
                  eventsIndex
                ];
              return <CellValue value={event?.eventDate} {...shortDateViewRules} />;
            }}
          />
          <TableSectionValuesPerForecast
            title={l('_InfusionAmount')}
            renderSpacer={false}
            testId={testId ? `${testId}-infusion-amount-${eventsIndex}` : undefined}
            renderCell={({ caseItem, forecastIndex }) => {
              if (forecastIndex !== 0) {
                return null;
              }
              const event =
                waterfallCasesResults[caseItem.caseId].historicInstitutionalAdditions?.[
                  eventsIndex
                ];
              return <CellValue value={event?.amount} {...amountRules} />;
            }}
          />
        </Fragment>
      ))}
    </>
  );
};

export default TableSectionHistoricalInfusions;
